import {
  fetchSessionForAuthRequest,
  selectOauth,
  selectSession,
  setOauthRedirectInitiated,
} from '@client/model';
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { SessionType } from '@shared';
import { ClipLoader } from 'react-spinners';
import { CheckCircle, TriangleAlert } from 'lucide-react';
import { useToast } from './hooks/use-toast';
import { capitalizeFirstLetter } from '@shared';

export enum OAuthRedirectPage {
  ADVISOR_PROFILE = 'advisorProfile',
  ADVISOR_ONBOARDING = 'advisorOnboarding',
  HOME = 'home',
}

export const OAUTH_REDIRECT_FROM_PAGE_KEY = 'oauth_redirect_from_page';

const getPageToNavigateFn = (
  pageToNavigateFn: Record<OAuthRedirectPage, () => void>,
): (() => void) => {
  const redirectPageKey = localStorage.getItem(
    OAUTH_REDIRECT_FROM_PAGE_KEY,
  ) as OAuthRedirectPage | null;

  if (!redirectPageKey || !pageToNavigateFn[redirectPageKey]) {
    return pageToNavigateFn[OAuthRedirectPage.HOME];
  }

  const redirectPageFn = pageToNavigateFn[redirectPageKey];
  return redirectPageFn;
};

export interface OAuthProviderConfig {
  provider: OAuthProvider;
  title: string;
  dispatchAction: (payload: { code: string; advisorId: string }) => any;
}

interface OAuthCallbackError {
  title: string;
  description: string;
}

// Standard OAuth2 authorization error codes (RFC 6749 §4.1.2.1), shared across
// providers. `providerName` is already capitalized by the caller.
const getSharedOauthErrorMessage = (
  code: string,
  providerName: string,
): OAuthCallbackError | null => {
  switch (code) {
    case 'access_denied':
      return {
        title: 'Permissions required',
        description: `You didn't grant the permissions Waterlily needs to connect your ${providerName} account. To send emails from your account, please try again and accept the requested permissions.`,
      };
    case 'invalid_scope':
      return {
        title: 'Requested permissions unavailable',
        description: `${providerName} rejected the permissions Waterlily requested. Please try again, or contact support if the problem continues.`,
      };
    case 'invalid_request':
    case 'unsupported_response_type':
      return {
        title: `Couldn't start the ${providerName} connection`,
        description: `There was a problem with the connection request to ${providerName}. Please try again, or contact support if the problem continues.`,
      };
    case 'unauthorized_client':
    case 'invalid_client':
      return {
        title: 'Connection not authorized',
        description: `Waterlily isn't authorized to connect to ${providerName} right now. Please contact support so we can look into it.`,
      };
    case 'server_error':
      return {
        title: `${providerName} had a problem`,
        description: `${providerName} ran into an error while connecting your account. This is usually temporary — please try again in a few minutes.`,
      };
    case 'temporarily_unavailable':
      return {
        title: `${providerName} is temporarily unavailable`,
        description: `${providerName} is temporarily unable to connect your account. Please try again in a few minutes.`,
      };
    default:
      return null;
  }
};

// Provider-specific error codes. These take precedence over the shared codes.
const getProviderOauthErrorMessage = (
  provider: OAuthProvider,
  code: string,
  providerName: string,
): OAuthCallbackError | null => {
  if (provider === 'microsoft') {
    switch (code) {
      case 'consent_required':
        return {
          title: 'Consent required',
          description: `Microsoft needs you to grant consent before Waterlily can connect your ${providerName} account. Please try again and accept the requested permissions.`,
        };
      case 'interaction_required':
      case 'login_required':
        return {
          title: 'Sign in to Microsoft again',
          description: `Microsoft needs you to sign in again before connecting your account. Please try again and complete the Microsoft sign-in.`,
        };
      default:
        return null;
    }
  }
  if (provider === 'google') {
    switch (code) {
      case 'admin_policy_enforced':
        return {
          title: 'Blocked by your organization',
          description: `Your Google Workspace administrator has restricted the permissions Waterlily requested, so this account can't be connected. Please contact your admin, or try a different account.`,
        };
      case 'org_internal':
        return {
          title: 'Account not allowed',
          description: `This account isn't permitted to connect to Waterlily. Please try a different Google account, or contact support.`,
        };
      case 'disallowed_useragent':
        return {
          title: 'Open in a standard browser',
          description: `Google blocked the sign-in because it wasn't opened in a standard browser. Please open Waterlily in Chrome, Safari, or another full browser and try again.`,
        };
      default:
        return null;
    }
  }
  return null;
};

// Generic OAuth callback component
export function OAuthCallback({ providerConfig }: { providerConfig: OAuthProviderConfig }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { error, status, redirectInitiated } = useSelector(selectOauth);
  const session = useSelector(selectSession);
  const advisorId = session.advisor?.advisorId;
  const hasAuthToken = session.sessionType !== SessionType.unauthenticated && session.sessionType !== SessionType.unknown;
  // We are still waiting for the server session to resolve. Distinct from
  // "auth failed" so we can keep showing a loader instead of erroring out
  // during the brief window between OAuth redirect and session fetch.
  const isResolvingSession = session.sessionType === SessionType.unknown || session.isLoading;
  const isAuthFailed = session.sessionType === SessionType.unauthenticated;
  const { toast } = useToast();
  const [callbackErrorCode, setCallbackErrorCode] = useState<string | null>(null);
  // An authorization code is single-use; the provider rejects a second exchange
  // with invalid_grant. Tracked in a ref so it survives re-renders and
  // StrictMode's double effect invocation, but resets on a genuine remount.
  const attemptedCodeRef = useRef<string | null>(null);
  const consentUrl = session.advisor?.advisorOauthSettings?.[providerConfig.provider]?.consentUrl;

  const getNavigationFn = () => {
    const pageToNavigateFn: Record<OAuthRedirectPage, () => void> = {
      [OAuthRedirectPage.ADVISOR_PROFILE]: () =>
        advisorId
          ? navigate(`/advisors/${advisorId}/settings?tab=communication`)
          : navigate('/'),
      [OAuthRedirectPage.ADVISOR_ONBOARDING]: () =>
        advisorId
          ? navigate(`/advisors/${advisorId}/onboarding/connect-email`)
          : navigate('/'),
      [OAuthRedirectPage.HOME]: () => navigate('/'),
    };
    return getPageToNavigateFn(pageToNavigateFn);
  };

  // Handle OAuth callback
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const callbackError = searchParams.get('error');
    if (callbackError) {
      console.error('OAuth error:', callbackError);
      // Surface the failure instead of silently redirecting. During onboarding
      // connect-email is optional, so an auto-redirect would bounce the user to
      // the dashboard and they'd never learn that the connection failed.
      setCallbackErrorCode(callbackError);
      return;
    }

    const code = searchParams.get('code');
    const advisorIdState = searchParams.get('state');
    if (!code || !advisorIdState || !hasAuthToken) {
      return;
    }

    // Exchanging is terminal either way: on failure the code is spent, so
    // retrying it can only ever fail again. Recovery means a fresh consent
    // round-trip, which the error card offers.
    if (attemptedCodeRef.current === code) {
      return;
    }
    attemptedCodeRef.current = code;

    dispatch(
      providerConfig.dispatchAction({
        code,
        advisorId: advisorIdState,
      }),
    );
  }, [location.search, providerConfig, hasAuthToken]);

  // Handle success
  useEffect(() => {
    if (status === 'complete' && !session.isLoading && !redirectInitiated) {
      dispatch(fetchSessionForAuthRequest()); // refresh session
      dispatch(setOauthRedirectInitiated(true));
      getNavigationFn()();
      toast({
        title: `${providerConfig.title}`,
        description: `You can now send emails from your ${capitalizeFirstLetter(providerConfig.provider)} account.`,
        variant: 'default',
        className: 'border-green-500 bg-green-50 text-green-800',
      });
    }
  }, [status, session.isLoading]);

  // Resolve a user-facing message for the callback error code. Provider-specific
  // codes win over the shared OAuth2 codes; unknown codes get a generic message.
  const getOauthCallbackError = (): OAuthCallbackError | null => {
    if (!callbackErrorCode) {
      return null;
    }
    const providerName = capitalizeFirstLetter(providerConfig.provider);
    return (
      getProviderOauthErrorMessage(providerConfig.provider, callbackErrorCode, providerName) ??
      getSharedOauthErrorMessage(callbackErrorCode, providerName) ?? {
        title: `Couldn't connect your ${providerName} account`,
        description: `We couldn't connect your ${providerName} account. Please try again, or contact support if the problem continues.`,
      }
    );
  };
  const oauthCallbackError = getOauthCallbackError();

  if (oauthCallbackError || status === 'error' || (isAuthFailed && status !== 'complete')) {
    const alertTitle = oauthCallbackError?.title ?? 'Authentication failed';
    const alertMessage =
      oauthCallbackError?.description ??
      (isAuthFailed
        ? 'Your session expired before we could connect your account. Please sign in again and retry.'
        : error);
    // A spent or rejected code can only be recovered by getting a new one, so
    // offer the consent round-trip. An expired session is the exception: they
    // need to sign in again first, and the redirect would just bounce back.
    const canRetryConsent = !!consentUrl && !isAuthFailed;
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="pt-6">
            <Alert className="mb-4 border-yellow-600 bg-yellow-50 text-yellow-900 [&>svg]:text-yellow-600">
              <TriangleAlert size={16} />
              <AlertTitle>{alertTitle}</AlertTitle>
              <AlertDescription className="text-yellow-900">{alertMessage}</AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2">
              {canRetryConsent && (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    window.location.href = consentUrl;
                  }}
                >
                  Try connecting again
                </Button>
              )}
              <Button
                variant={canRetryConsent ? 'outline' : 'default'}
                className="w-full"
                onClick={() => getNavigationFn()()}
              >
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md shadow-lg">
        {status === 'complete' ? (
          <CardContent className="pt-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <CheckCircle className="h-12 w-12 text-green-500" />
              <CardTitle>{providerConfig.title}</CardTitle>
              <CardDescription className="md:text-base">Redirecting you back...</CardDescription>
            </div>
          </CardContent>
        ) : (
          <CardContent className="pt-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <ClipLoader
                color="#3b82f6"
                size={48}
              />
              <CardTitle>
                {isResolvingSession ? 'Verifying your session' : 'Processing Authentication'}
              </CardTitle>
              <CardDescription>
                {isResolvingSession
                  ? 'Hang tight — finishing sign-in before we connect your account...'
                  : 'Please wait while we connect your account...'}
              </CardDescription>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
